import { format, isSameDay, parseISO } from 'date-fns';

import { logger } from '../../datadog';
import { PROVIDER_CONSENT_MODAL_DISMISSED_DATE_KEY } from './constants';

export type GetItemFn = (key: string) => string | null;

const defaultGetItem: GetItemFn = (key: string) => window.localStorage.getItem(key);

/**
 * Determines if the provider consent modal should be shown based on whether it
 * was already dismissed today.
 * @param getItem - Optional override for localStorage.getItem (for testing)
 * @param now - Optional override for current date (for testing)
 * @returns Whether or not the modal should be shown.
 */
export function shouldShowModalForProvider(getItem: GetItemFn = defaultGetItem, now: Date = new Date()): boolean {
  const dismissedDate = getItem(PROVIDER_CONSENT_MODAL_DISMISSED_DATE_KEY);
  if (!dismissedDate) return true;

  try {
    const parsed = parseISO(dismissedDate);
    return !isSameDay(parsed, now);
  } catch (error) {
    logger.warn('Found invalid value stored for PROVIDER_CONSENT_MODAL_DISMISSED_DATE_KEY:', { dismissedDate, error });
    return true;
  }
}

/**
 * Records that the provider has dismissed the consent modal today. This
 * prevents the modal from appearing again until the next calendar day.
 */
export function recordModalDismissalForProvider(): void {
  const today = format(new Date(), 'yyyy-MM-dd');
  window.localStorage.setItem(PROVIDER_CONSENT_MODAL_DISMISSED_DATE_KEY, today);
}

/**
 * Hook for managing when the provider has dismissed the consent modal.
 * @returns Stable callback functions for checking and recording dismissal.
 */
export function useProviderConsentModalDismissal() {
  return {
    shouldShowModalForProvider,
    recordModalDismissalForProvider,
  };
}
